import React from "react";
import { useSelector } from "react-redux";
import { useLazyCheckoutItemQuery } from "../../../src/taskpane/components/_v2/redux/api/sharepoint/api-sharepoint-slice";
import { ComponentTypes } from "../../../src/taskpane/config/config";
import { OneNoteProgId, SharePointFolderContentTypeIds } from "../../../src/taskpane/config/constants";
import { RootState } from "../../../src/taskpane/redux";
import authModule from "../../../utilities/AuthModule";
import { LanguageContext } from "../../../utilities/LocalizationModule";
import { menuItemIconStyles } from "../../../src/taskpane/config/controlStyles";
import { ArrowCircleDownRight20Regular } from "../../../src/taskpane/components/_v2/components/SvgIcons/ArrowCircleDownRight20Regular";

export function useMenuItem_Checkout(
    selectedItems: any[],
    waitFor: boolean,
    setWaitFor: (waitFor: boolean) => void,
    isInOneDrive: boolean,
    setGridItemIsBusy: (items: any[], isBusy: boolean) => void,
    updateGridItems: (items: any[]) => void,
    setIsCMErrorModalOpen: (isCMErrorModalOpen: boolean) => void,
    setErrorMessage?: (errorMessage: string) => void,
) {
    const languageStrings = React.useContext(LanguageContext);
    const currentDirectory = useSelector((state: RootState) => (
        state.workingDirectories.workingDirectories.length > 0
            ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1]
            : null
    ));

    const [checkoutItem] = useLazyCheckoutItemQuery();

    const onCheckoutItem = React.useCallback(() => {
        (async () => {
            setGridItemIsBusy(selectedItems, true);
            setWaitFor(true);

            await Promise.all(selectedItems.map(async item => {
                try {
                    await checkoutItem({
                        siteUrl: currentDirectory.webUrl,
                        uniqueId: item.UniqueId.replace(/[{}]/g, ""),
                        accessToken: await authModule.getSharePointAccessToken(),
                    }).unwrap();
                } catch (error) {
                    console.error("Failed to checkout item:", error);
                    setIsCMErrorModalOpen(true);
                    setErrorMessage(error?.data?.['odata.error']?.message?.value || "An unknown error occurred during checkout.");
                }
            }));

            updateGridItems(selectedItems);
            setWaitFor(false);
        })();
    }, [
        selectedItems,
        currentDirectory,
        checkoutItem,
        setGridItemIsBusy,
        setWaitFor,
        updateGridItems,
        setIsCMErrorModalOpen,
        setErrorMessage
    ]);

    const menuItem_Checkout = React.useMemo(() => {

        const hasSelection = selectedItems.length > 0;

        const allItemsAreAvailable = selectedItems.every(x => !x?.CheckoutUser);

        const selectionContainsNoFolders = !selectedItems.some(x =>
            x.ContentTypeId?.startsWith(SharePointFolderContentTypeIds.folder) || x.ProgId === OneNoteProgId
        );

        const userCanEditAllItems = !selectedItems.some(x => x?.EffectiveBasePermissions?.canEditListItem === false);

        const shouldShowMenuItem =
            !isInOneDrive &&
            hasSelection &&
            currentDirectory?.type !== ComponentTypes.SharePointList &&
            allItemsAreAvailable &&
            selectionContainsNoFolders &&
            userCanEditAllItems;

        if (shouldShowMenuItem) {
            return {
                key: 'checkout',
                disabled: waitFor === true,
                text: languageStrings.CheckOut,
                // iconProps: { iconName: "CheckedOutByOther12" },
                iconProps: {},
                onRenderIcon: () => <ArrowCircleDownRight20Regular className={menuItemIconStyles} />,
                onClick: onCheckoutItem
            };
        }

        return null;

    }, [
        selectedItems,
        waitFor,
        isInOneDrive,
        currentDirectory,
        languageStrings,
        onCheckoutItem
    ]);

    return {
        menuItem_Checkout
    };
}